// Tax (M5 plan D3, D12): the BAS figures to copy into ATO Online services, each one opening the
// transactions behind it, and the GST settings tucked underneath.
import { type FormEvent, useEffect, useState } from 'react';
import { GstSettings, type Settings } from '../accountant/GstSettings.js';
import { useOrgData } from '../accountant/useOrgData.js';
import type { Api } from '../api.js';
import { day, dollars } from '../format.js';

interface Period {
  start: string;
  end: string;
  label: string;
}
interface Lodgement {
  periodStart: string;
  periodEnd: string;
  net: string;
  receiptNumber: string;
  lodgedOn: string;
}
interface Line {
  journalId: string;
  date: string;
  description: string | null;
  amount: string;
  gst: string;
}
interface Worksheet {
  period: Period;
  settings: Settings;
  figures: { g1: string; oneA: string; oneB: string; fiveA: string; net: string };
  toCopy: { g1: string; oneA: string; oneB: string; fiveA: string; net: string };
  instalmentIncome: string | null;
  transactions: { g1: Line[]; oneA: Line[]; oneB: Line[] };
}

export function Tax({ api, orgId }: { api: Api; orgId: string }) {
  const list = useOrgData<{
    periods: Period[];
    lodgements: Lodgement[];
    lockDate: string | null;
    today: string;
  }>(api, orgId, '/bas');
  const [start, setStart] = useState('');
  const periods = list.data?.periods ?? [];
  const today = list.data?.today ?? '';
  const lodged = new Map((list.data?.lodgements ?? []).map((l) => [l.periodStart, l]));
  // Default: the latest finished period not lodged yet, else the current one.
  const chosen =
    start ||
    (periods.find((p) => p.end < today && !lodged.has(p.start)) ?? periods[0])?.start ||
    '';

  if (list.error) return <p className="error">{list.error}</p>;
  if (!list.data) return <p className="muted">Loading</p>;
  return (
    <section className="stack">
      <h1>Tax</h1>
      <label>
        Activity statement (BAS) for
        <select value={chosen} onChange={(e) => setStart(e.target.value)}>
          {periods.map((p) => (
            <option key={p.start} value={p.start}>
              {p.label}
              {lodged.has(p.start) ? ' (lodged)' : p.end >= today ? ' (in progress)' : ''}
            </option>
          ))}
        </select>
      </label>
      {chosen && (
        <BasWorksheet
          key={chosen}
          api={api}
          orgId={orgId}
          start={chosen}
          lodgement={lodged.get(chosen)}
          canLodge={
            !lodged.has(chosen) &&
            (periods.find((p) => p.start === chosen)?.end ?? today) < today &&
            (!list.data.lockDate || chosen > list.data.lockDate)
          }
          beforeFoderis={
            !!list.data.lockDate &&
            (periods.find((p) => p.start === chosen)?.end ?? '') <= list.data.lockDate
          }
          onLodged={list.reload}
        />
      )}
      <GstCheck api={api} orgId={orgId} />
      <GstSettings api={api} orgId={orgId} onSaved={list.reload} />
    </section>
  );
}

function BasWorksheet(props: {
  api: Api;
  orgId: string;
  start: string;
  lodgement: Lodgement | undefined;
  canLodge: boolean;
  /** Before the cutover: lodged from Xero, recorded here with Xero's figures (M6 plan D3). */
  beforeFoderis: boolean;
  onLodged: () => void;
}) {
  const { api, orgId, start, lodgement } = props;
  const ws = useOrgData<Worksheet>(api, orgId, `/bas/${start}`);
  const [open, setOpen] = useState<'g1' | 'oneA' | 'oneB' | null>(null);
  if (ws.error) return <p className="error">{ws.error}</p>;
  if (!ws.data) return <p className="muted">Working out the figures</p>;
  const { figures, toCopy, settings } = ws.data;
  const rows: [string, string, keyof Worksheet['toCopy'], 'g1' | 'oneA' | 'oneB' | null][] =
    settings.gstRegistered
      ? [
          ['G1', 'Total sales (including GST)', 'g1', 'g1'],
          ['1A', 'GST on sales', 'oneA', 'oneA'],
          ['1B', 'GST on purchases', 'oneB', 'oneB'],
        ]
      : [];
  if (settings.paygMethod !== 'none')
    rows.push(['5A', 'PAYG income tax instalment', 'fiveA', null]);
  const net = BigInt(toCopy.net);
  return (
    <div className="stack">
      <p className="muted">
        Copy these into ATO Online services (or give them to your BAS agent). The ATO asks for whole
        dollars, so the cents are left off.{' '}
        {settings.gstRegistered && 'Answer "Yes" to "GST included in total sales".'}
      </p>
      <table className="table">
        <tbody>
          {rows.map(([label, name, key, drill]) => (
            <tr key={label}>
              <td>
                <strong>{label}</strong>
              </td>
              <td>{name}</td>
              <td className="num">
                <CopyAmount value={toCopy[key]} />
              </td>
              <td className="num muted">
                {drill ? (
                  <button
                    type="button"
                    className="link"
                    onClick={() => setOpen(open === drill ? null : drill)}
                  >
                    {dollars(figures[key])}
                  </button>
                ) : (
                  dollars(figures[key])
                )}
              </td>
            </tr>
          ))}
          <tr className="total">
            <td>
              <strong>9</strong>
            </td>
            <td>{net >= 0n ? 'You pay the ATO' : 'The ATO refunds you'}</td>
            <td className="num">
              <CopyAmount value={(net < 0n ? -net : net).toString()} />
            </td>
            <td className="num muted">{dollars(figures.net.replace('-', ''))}</td>
          </tr>
        </tbody>
      </table>
      {ws.data.instalmentIncome && (
        <p className="muted">
          Instalment income for the period: {dollars(ws.data.instalmentIncome)} at{' '}
          {settings.paygRate}%.
        </p>
      )}
      {settings.paygMethod === 'none' && (
        <p className="muted">PAYG withheld (W1, W2, 4) is nil: there is no payroll in Foderis.</p>
      )}
      {lodgement && (
        <p>
          Lodged on {day(lodgement.lodgedOn)}, ATO receipt {lodgement.receiptNumber}:{' '}
          {dollars(lodgement.net.replace('-', ''))}{' '}
          {lodgement.net.startsWith('-') ? 'refunded' : 'to pay'}. This period is locked;
          corrections go in a later BAS.
        </p>
      )}
      {props.canLodge && (
        <MarkLodged
          api={api}
          orgId={orgId}
          start={start}
          toCopy={toCopy}
          onLodged={props.onLodged}
        />
      )}
      {props.beforeFoderis && !lodgement && (
        <MarkLodged
          api={api}
          orgId={orgId}
          start={start}
          toCopy={{ g1: '', oneA: '', oneB: '', fiveA: '0', net: '' }}
          beforeFoderis
          onLodged={props.onLodged}
        />
      )}
      {open && (
        <table className="table">
          <caption>Behind {open === 'g1' ? 'G1' : open === 'oneA' ? '1A' : '1B'}</caption>
          <tbody>
            {ws.data.transactions[open].map((t) => (
              <tr key={`${t.journalId}-${t.amount}-${t.gst}`}>
                <td>{day(t.date)}</td>
                <td>{t.description}</td>
                <td className="num">{dollars(t.amount)}</td>
                <td className="num muted">
                  {t.gst === '0.00' ? 'No GST' : `GST ${dollars(t.gst)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CopyAmount({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const shown = `$${value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  return (
    <button
      type="button"
      className="link"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }}
    >
      <strong>{copied ? 'Copied' : shown}</strong>
    </button>
  );
}

function MarkLodged(props: {
  api: Api;
  orgId: string;
  start: string;
  toCopy: Worksheet['toCopy'];
  beforeFoderis?: boolean;
  onLodged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [figures, setFigures] = useState(props.toCopy);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [lodgedOn, setLodgedOn] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)}>
        {props.beforeFoderis ? 'Record the BAS lodged from Xero' : 'I have lodged this BAS'}
      </button>
    );
  async function lodge(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await props.api.send(
        props.orgId,
        'POST',
        `/bas/${props.start}/${props.beforeFoderis ? 'lodged-before-foderis' : 'lodge'}`,
        {
          ...figures,
          receiptNumber,
          lodgedOn,
        },
      );
      props.onLodged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the lodgement.');
    } finally {
      setBusy(false);
    }
  }
  const field = (label: string, key: keyof Worksheet['toCopy']) => (
    <label>
      {label} (whole dollars, as lodged)
      <input
        inputMode="numeric"
        value={figures[key]}
        onChange={(e) => setFigures({ ...figures, [key]: e.target.value })}
      />
    </label>
  );
  return (
    <form className="stack bank-card" onSubmit={lodge}>
      <h2>Record the lodged BAS</h2>
      <p className="muted">
        Check the figures match what was lodged. Foderis then records what you owe the ATO and locks
        the period, so nothing in it can change.
      </p>
      <div className="grid-2">
        {field('G1', 'g1')}
        {field('1A', 'oneA')}
        {field('1B', 'oneB')}
        {props.toCopy.fiveA !== '0' && field('5A', 'fiveA')}
        <label>
          ATO receipt number
          <input
            value={receiptNumber}
            onChange={(e) => setReceiptNumber(e.target.value)}
            required
          />
        </label>
        <label>
          Lodged on
          <input
            type="date"
            value={lodgedOn}
            onChange={(e) => setLodgedOn(e.target.value)}
            required
          />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="title-row">
        <button type="submit" disabled={busy}>
          Record and lock the period
        </button>
        <button type="button" className="link" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

interface Reconciliation {
  lockDate: string | null;
  gstAccountBalance: string;
  gstOfOpenPeriods: string;
  difference: string;
  adjustments: { journal_id: string; date: string; memo: string | null; amount: string }[];
  uncoded: {
    journal_id: string;
    date: string;
    memo: string | null;
    account: string;
    amount: string;
  }[];
}

/** GST check (M5 plan D6): the GST account against the GST of periods not lodged yet. */
function GstCheck({ api, orgId }: { api: Api; orgId: string }) {
  const rec = useOrgData<Reconciliation>(api, orgId, '/tax/gst-reconciliation');
  if (!rec.data) return null;
  const r = rec.data;
  const fine = r.difference === '0.00' && r.uncoded.length === 0;
  return (
    <div className="stack">
      <h2>GST check</h2>
      {fine ? (
        <p>All GST is accounted for: the GST account matches the periods not yet lodged.</p>
      ) : (
        <>
          {r.difference !== '0.00' && (
            <p className="error">
              The GST account is out by {dollars(r.difference)} against the GST of the periods not
              yet lodged. These entries changed it directly:
            </p>
          )}
          {r.adjustments.map((a) => (
            <p key={`${a.journal_id}-${a.amount}`} className="muted">
              {day(a.date)} {a.memo} {dollars(a.amount)}
            </p>
          ))}
          {r.uncoded.length > 0 && (
            <p className="error">
              {r.uncoded.length} income or expense amounts have no GST treatment chosen.
            </p>
          )}
        </>
      )}
    </div>
  );
}
